import { useState, useRef, useMemo, useEffect } from 'react'
import { Agent } from '@atproto/api'
import { useVerificationPreferences } from '@/hooks/useVerificationPreferences'
import { useAuth } from '@/hooks/useAuth'
import { usePlatformGuard } from '@/hooks/usePlatformGuard'
import { resolveVerificationsBatchOnServer } from '@/services/trustedVerifiersService'
import type { VerificationRecord } from '@/types/verification'

export type VerificationMap = {
    [identifier: string]: { isVerified: boolean; verifiers: VerificationRecord[] }
}

const BATCH_SIZE = 50

export function useBatchVerification(identifiers: string[]): {
    verificationMap: VerificationMap
    isLoading: boolean
} {
    const { preferences } = useVerificationPreferences()
    const { session } = useAuth()
    const isCompatible = usePlatformGuard('atproto')
    const agent = useMemo(() => (session ? new Agent(session) : null), [session])

    const [verificationMap, setVerificationMap] = useState<VerificationMap>({})
    const [loadingCount, setLoadingCount] = useState(0)
    const fetchedDidsRef = useRef<Set<string>>(new Set())
    const prevTrustedVerifiersKeyRef = useRef<string>('')

    const uniqueSortedDids = useMemo(() => [...new Set(identifiers)].sort(), [identifiers])

    const enabled = !preferences.hideAll && preferences.enabled && !!agent && isCompatible

    const trustedVerifiersKey = useMemo(
        () =>
            preferences.trustedVerifiers
                .map(v => v.did)
                .sort()
                .join(','),
        [preferences.trustedVerifiers]
    )

    // Reset accumulated state when the session changes
    useEffect(() => {
        fetchedDidsRef.current = new Set()
        setVerificationMap({})
        setLoadingCount(0)
    }, [session?.did])

    // Guard against loadingCount getting stuck when verification is disabled mid-flight.
    // If preferences.hideAll becomes true while a fetch is in-flight, the cleanup may race
    // with the fetch's finally block. Resetting to 0 here ensures isLoading never stays
    // true after verification is disabled.
    // Also clear fetchedDidsRef and verificationMap so that when enabled is toggled back
    // on, badges reload fresh instead of staying blank.
    useEffect(() => {
        if (!enabled) {
            setLoadingCount(0)
            fetchedDidsRef.current = new Set()
            setVerificationMap({})
        }
    }, [enabled])

    useEffect(() => {
        if (!enabled || !agent) return

        // When trustedVerifiers change, clear the cache so all DIDs are re-fetched
        // with the updated verifier set. Resetting the ref here (synchronously, before
        // computing newDids) keeps the reset and re-fetch atomic within this effect run,
        // avoiding a fragile cross-effect ordering dependency.
        if (prevTrustedVerifiersKeyRef.current !== trustedVerifiersKey) {
            prevTrustedVerifiersKeyRef.current = trustedVerifiersKey
            fetchedDidsRef.current = new Set()
            setVerificationMap({})
        }

        const newDids = uniqueSortedDids.filter(d => !fetchedDidsRef.current.has(d))
        if (newDids.length === 0) return

        // Mark as fetched immediately to prevent duplicate requests on re-render
        newDids.forEach(d => fetchedDidsRef.current.add(d))
        setLoadingCount(c => c + 1)

        let resolved = false

        const fetchVerifications = async () => {
            try {
                const chunks: string[][] = []
                for (let i = 0; i < newDids.length; i += BATCH_SIZE) {
                    chunks.push(newDids.slice(i, i + BATCH_SIZE))
                }
                const results = await Promise.all(
                    chunks.map(async chunk => ({
                        chunk,
                        result: await resolveVerificationsBatchOnServer(agent, chunk),
                    }))
                )
                const merged: VerificationMap = {}
                for (const { chunk, result } of results) {
                    if (result !== null) {
                        Object.assign(merged, result)
                    } else {
                        chunk.forEach(d => fetchedDidsRef.current.delete(d))
                    }
                }
                if (!resolved) {
                    setVerificationMap(prev => ({ ...prev, ...merged }))
                }
            } finally {
                if (!resolved) {
                    resolved = true
                    setLoadingCount(c => Math.max(0, c - 1))
                }
            }
        }

        fetchVerifications().catch(console.error)

        return () => {
            if (!resolved) {
                resolved = true
                newDids.forEach(d => fetchedDidsRef.current.delete(d))
                setLoadingCount(c => Math.max(0, c - 1))
            }
        }
    }, [uniqueSortedDids, enabled, agent, trustedVerifiersKey])

    return {
        verificationMap,
        isLoading: enabled ? loadingCount > 0 : false,
    }
}
